"use client";

import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { mutationOptions, useMutation } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { queueManagementApi } from "../api";
import type { CreateQueueInput } from "../types";

interface CreateQueueDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	venueId: string;
}

const createQueueSchema = z.object({
	name: z
		.string()
		.min(1, "Queue name is required")
		.max(100, "Queue name must not exceed 100 characters"),
	voidPrintsEnabled: z.boolean(),
});

type CreateQueueFormValues = z.infer<typeof createQueueSchema>;

export function CreateQueueDialog({
	open,
	onOpenChange,
	tenant,
	venueId,
}: CreateQueueDialogProps) {
	const mutation = useMutation(
		mutationOptions({
			...queueManagementApi.createQueueOptions(tenant),
			onSuccess: (result, _variables, _onMutateResult, context) => {
				if (result.success) {
					toast.success("Queue created successfully.");
					context.client.invalidateQueries({
						queryKey: [tenant, "queues"],
					});
					handleClose(false);
				} else {
					const errorDetails = result.details as
						{ detail?: string } | undefined;
					toast.error(
						errorDetails?.detail || "Failed to create queue. Please try again.",
					);
				}
			},
			onError: () => {
				toast.error("Unable to create queue. Please try again.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			name: "",
			voidPrintsEnabled: true,
		} as CreateQueueFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: createQueueSchema,
		},
		onSubmit: async ({ value }) => {
			const input: CreateQueueInput = {
				name: value.name.trim(),
				venueId,
				voidPrintsEnabled: value.voidPrintsEnabled,
			};
			await mutation.mutateAsync(input);
		},
	});

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			mutation.reset();
		}
		onOpenChange(o);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Create Queue</DialogTitle>
					<DialogDescription>
						Create a printing queue, then add its printers on the queue&apos;s
						page.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{mutation.isError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Unable to create queue. Please try again.
							</AlertDescription>
						</Alert>
					)}

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Queue Name</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g., Kitchen, Bar, Pizza Oven"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormDescription>
									A name to identify this printing queue.
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="voidPrintsEnabled">
						{(field) => (
							<field.FormItem>
								<div className="flex items-center space-x-2">
									<Checkbox
										id="voidPrintsEnabled"
										checked={field.state.value}
										onCheckedChange={(checked) =>
											field.handleChange(checked === true)
										}
									/>
									<Label htmlFor="voidPrintsEnabled" className="text-sm">
										Enable void prints
									</Label>
								</div>
								<field.FormDescription>
									When enabled, removing a sent item will print a void ticket to
									this queue&apos;s printers.
								</field.FormDescription>
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
						>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => handleClose(false)}
										disabled={mutation.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Create Queue
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
